import { useCallback, useEffect, useState } from 'react';

/**
 * Un onglet ouvert avant un déploiement garde l'ancien bundle aussi longtemps
 * qu'il n'est pas rechargé — des jours chez un avocat. Il continue alors de
 * parler au backend avec un protocole périmé : c'est ce qui a laissé des
 * canvas bloqués en `mount_readiness_missing` après la release du 2026-09-27
 * (les bundles antérieurs n'annoncent pas la readiness versionnée).
 *
 * `version.json` est émis par `vite.config.ts` avec la même valeur que
 * `__HECTOR_APPLICATION_VERSION__` (le commit Vercel). Une différence signifie
 * qu'un bundle plus récent est en ligne.
 */
const INTERVALLE_DE_VERIFICATION_MS = 5 * 60 * 1000;
const CLE_MASQUAGE = 'hector:nouvelle-version-masquee';

async function lireVersionDeployee(): Promise<string | null> {
  try {
    // `no-store` + paramètre : ni le navigateur ni un CDN ne doivent resservir
    // une ancienne réponse, sinon l'onglet ne verrait jamais la nouvelle version.
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'version' in body && typeof body.version === 'string') {
      return body.version;
    }
    return null;
  } catch {
    // En dev, `version.json` n'existe pas (fallback SPA → HTML) ; hors ligne,
    // le fetch échoue. Dans les deux cas : pas de bandeau, on réessaiera.
    return null;
  }
}

function lireMasquage(): string | null {
  try {
    return sessionStorage.getItem(CLE_MASQUAGE);
  } catch {
    return null;
  }
}

/**
 * Détecte qu'une version plus récente de l'app est déployée.
 * Le masquage vaut pour la version vue : un déploiement suivant réaffiche le bandeau.
 */
export function useNouvelleVersionDisponible(): { disponible: boolean; masquer: () => void } {
  const [versionDeployee, setVersionDeployee] = useState<string | null>(null);
  const [versionMasquee, setVersionMasquee] = useState<string | null>(lireMasquage);

  useEffect(() => {
    let annule = false;
    const verifier = async () => {
      const version = await lireVersionDeployee();
      if (!annule && version !== null && version !== __HECTOR_APPLICATION_VERSION__) {
        setVersionDeployee(version);
      }
    };
    const surRetourDansLOnglet = () => {
      if (document.visibilityState === 'visible') void verifier();
    };
    void verifier();
    const timer = window.setInterval(() => void verifier(), INTERVALLE_DE_VERIFICATION_MS);
    document.addEventListener('visibilitychange', surRetourDansLOnglet);
    return () => {
      annule = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', surRetourDansLOnglet);
    };
  }, []);

  const masquer = useCallback(() => {
    if (versionDeployee === null) return;
    setVersionMasquee(versionDeployee);
    try {
      sessionStorage.setItem(CLE_MASQUAGE, versionDeployee);
    } catch {
      // Stockage indisponible : le masquage vaut seulement pour ce rendu.
    }
  }, [versionDeployee]);

  return { disponible: versionDeployee !== null && versionDeployee !== versionMasquee, masquer };
}
